import { DateTime } from 'luxon';

import { parseLessonInstant } from 'services/LessonTimeService';

/** How long before the start time the room may be entered. */
export const JOIN_OPENS_MINUTES_BEFORE = 90;
/**
 * Сколько минут после конца занятия кнопка ещё работает.
 *
 * Сервер принимает клик и ставит «был» до `end + ATTENDANCE_AUTO_GRACE_MINUTES`
 * (tmsis/settings.py, по умолчанию 30). Раньше окно закрывалось ровно в конце
 * занятия: студент, у которого под конец отвалился Zoom, видел мёртвую кнопку
 * «Zajęcia zakończone», хотя сервер его пустил бы. Держать равным серверной
 * настройке.
 */
export const JOIN_GRACE_MINUTES_AFTER = 30;
/** Длительность занятия, если сервер не прислал конец (обычное занятие — 3 ч). */
const DEFAULT_DURATION_HOURS = 3;

export interface IJoinWindow {
  open: boolean;
  ended: boolean;
  startsAt: DateTime | null;
}

/**
 * The lesson's own time window. `hasJoinLink` alone is group-wide, so without
 * this the button rendered fully enabled on every lesson — a student browsing
 * lesson 3 a week early could click it, record a join click and land in a dead
 * room.
 *
 * ТЗ 0100: окно строится от моментов `startsAt`/`endsAt` (ISO с офсетом,
 * время урока — варшавское), а не от даты и сырого времени: сравнение моментов
 * не зависит ни от пояса браузера, ни от перехода на зимнее время. Нет или
 * битое начало — окно ОТКРЫТО, а не блокирует вход, о котором мы не можем
 * судить.
 *
 * Окно не зависит ни от посещаемости, ни от прошлых кликов: повторный вход
 * после выхода из Zoom обязан работать так же, как первый.
 */
export const buildJoinWindow = (
  now: DateTime,
  startsAt?: string | null,
  endsAt?: string | null,
): IJoinWindow => {
  const start = parseLessonInstant(startsAt);
  if (!start) {
    return { open: true, ended: false, startsAt: null };
  }
  let end = parseLessonInstant(endsAt) ?? start.plus({ hours: DEFAULT_DURATION_HOURS });
  // Правило сервера (groups/services/lesson_time.py): конец РАНЬШЕ начала —
  // занятие через полночь, конец на следующий день; конец РАВЕН началу —
  // занятие нулевой длины, НЕ следующий день. Сервер уже присылает `endsAt`
  // перенесённым, так что сдвиг ниже — только страховка для `end < start`.
  // Равенство не трогаем: иначе кнопка жила бы до завтра, а сервер после
  // `start + 30 мин` клики уже не засчитывает.
  if (end < start) {
    end = end.plus({ days: 1 });
  }
  const closesAt = end.plus({ minutes: JOIN_GRACE_MINUTES_AFTER });
  return {
    open: now >= start.minus({ minutes: JOIN_OPENS_MINUTES_BEFORE }) && now <= closesAt,
    ended: now > closesAt,
    startsAt: start,
  };
};
